import { useState } from 'react';
import { Modal } from '../common/Modal';
import { AlertTriangle, X } from 'lucide-react';
import type { Certificate } from '../../hooks/useCertificates';
import { SeletorDeArquivo } from '../common/SeletorDeArquivo';
import {
  AVISO_VALIDADE_JURIDICA,
  FINALIDADE_MAXIMO,
  avisoDoPdf,
  baixarArquivo,
  formularioAssinaturaVazio,
  montarFormDataAssinatura,
  nomeDoAssinado,
  podeAssinar,
  type FormularioAssinatura,
} from '../../utils/signPdfForm';

interface CertificateSignModalProps {
  /** Certificado já escolhido (botão do cartão do certificado). */
  certificate?: Certificate | null;
  /** Etapa 24: sem `certificate`, a pessoa escolhe entre estes (aba "Assinaturas"). */
  certificados?: Certificate[];
  onClose: () => void;
  /** Envia o formulário pronto (file, purpose) com o certificado escolhido e devolve o PDF assinado. */
  onSign: (dados: FormData, certificateId: string) => Promise<Blob>;
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-bg-root)',
  border: '1px solid var(--color-border-default)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
};

const labelStyle: React.CSSProperties = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' };

/**
 * Etapa 16: assinar um PDF com o certificado. O PDF escolhido e o assinado vivem só aqui (memória);
 * o assinado vira download e o modal fecha.
 */
export function CertificateSignModal({ certificate, certificados = [], onClose, onSign }: CertificateSignModalProps) {
  const [form, setForm] = useState<FormularioAssinatura>(formularioAssinaturaVazio);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // Com um só certificado disponível, ele já vem escolhido.
  const [escolhido, setEscolhido] = useState<string>(certificate?.id ?? (certificados.length === 1 ? certificados[0].id : ''));
  const certificateId = certificate?.id ?? escolhido;

  const avisoArquivo = avisoDoPdf(form.file);
  const habilitado = podeAssinar(form) && certificateId !== '' && !isBusy;

  const fechar = () => {
    setForm(formularioAssinaturaVazio());
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!habilitado || !form.file) return;
    setIsBusy(true);
    setError(null);
    try {
      const assinado = await onSign(montarFormDataAssinatura(form), certificateId);
      baixarArquivo(assinado, nomeDoAssinado(form.file.name));
      fechar();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Erro ao assinar o PDF.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal pedirParaFechar={onClose} protegerDigitacao>
      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '520px', padding: 'var(--space-8)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>Assinar PDF</h2>
          <button type="button" onClick={fechar} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>
        {certificate && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', overflowWrap: 'anywhere' }}>
            Certificado: {certificate.label}
          </p>
        )}

        <div
          role="note"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-4)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', color: 'var(--color-text-primary)', lineHeight: 1.5 }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0, color: '#f59e0b', marginTop: 2 }} />
          <span>{AVISO_VALIDADE_JURIDICA}</span>
        </div>

        {error && (
          <div role="alert" style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {!certificate && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label htmlFor="sign-certificate" style={labelStyle}>Certificado</label>
              <select
                id="sign-certificate"
                value={escolhido}
                onChange={(e) => setEscolhido(e.target.value)}
                style={{ ...inputStyle, minWidth: 0, width: '100%' }}
              >
                <option value="" disabled>Escolha o certificado</option>
                {certificados.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.holder_name && c.holder_name !== c.label ? `${c.label} · ${c.holder_name}` : c.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="sign-file" style={labelStyle}>Arquivo PDF</label>
            <SeletorDeArquivo
              id="sign-file"
              accept=".pdf,application/pdf"
              dica=".pdf"
              arquivo={form.file}
              onChange={(file) => setForm((f) => ({ ...f, file }))}
            />
            {avisoArquivo && (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>{avisoArquivo}</span>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="sign-purpose" style={labelStyle}>Finalidade</label>
            <input
              id="sign-purpose"
              value={form.purpose}
              maxLength={FINALIDADE_MAXIMO}
              placeholder="Ex.: petição inicial do processo X"
              onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
            <button type="button" onClick={fechar} style={{ flex: 1, padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!habilitado}
              style={{ flex: 1, padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: habilitado ? 'pointer' : 'not-allowed', opacity: habilitado ? 1 : 0.6 }}
            >
              {isBusy ? 'Assinando...' : 'Assinar'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
